/** Small previews never gate the real photo or the purchase controls. */
if (!customElements.get('product-image-loader')) {
  customElements.define('product-image-loader', class extends HTMLElement {
    #controller;
    #observer;
    #slideObserver;
    #image;
    #timeout;
    #decodingSource;
    #decodedSource;

    connectedCallback() {
      this.#controller = new AbortController();
      const { signal } = this.#controller;
      document.addEventListener('product-navigation-settled', () => this.#reveal(), { signal });
      queueMicrotask(() => {
        if (signal.aborted) return;
        const preview = this.querySelector('.product-media__preview');
        try {
          const cached = JSON.parse(sessionStorage.getItem('chema-product-preview') || 'null');
          if (cached && preview && cached.path === location.pathname && Date.now() - cached.time < 15000) {
            const candidate = new URL(cached.src, location.href);
            const expected = new URL(preview.src, location.href);
            if (candidate.protocol === expected.protocol && candidate.host === expected.host && candidate.pathname === expected.pathname) {
              // Decode separately: replacing the visible tiny fallback's src
              // before its replacement is ready can create an empty frame.
              const warm = new Image();
              warm.decoding = 'async';
              warm.src = candidate.href;
              const commit = async () => {
                try { if (warm.decode) await warm.decode(); } catch { return; }
                if (signal.aborted || !warm.naturalWidth || this.hasAttribute('data-image-ready')) return;
                preview.removeAttribute('srcset');
                preview.src = candidate.href;
                this.setAttribute('data-navigation-preview', '');
              };
              if (warm.complete) void commit();
              else warm.addEventListener('load', commit, { once: true, signal });
            }
          }
        } catch { /* Storage is optional. */ }
        this.querySelector('.product-media__retry')?.addEventListener('click', (event) => {
          event.stopPropagation();
          this.load();
          if (this.#image) this.#image.src = this.#image.getAttribute('src');
        }, { signal });
        // Stacked inactive slides intersect too: only selection may load them.
        const slide = this.closest('slideshow-slide');
        if (this.querySelector('template[data-product-display-image]')) {
          const activate = () => {
            if (slide?.getAttribute('aria-hidden') === 'false') this.load();
          };
          if (slide) {
            this.#slideObserver = new MutationObserver(activate);
            this.#slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
            activate();
          }
          return;
        }
        this.#observeImage();
        if (slide?.getAttribute('aria-hidden') === 'true') {
          this.#slideObserver = new MutationObserver(() => {
            if (slide.getAttribute('aria-hidden') === 'false') this.load();
          });
          this.#slideObserver.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
          return;
        }
        if (this.#image?.loading === 'eager') this.load();
        else if ('IntersectionObserver' in window) {
          this.#observer = new IntersectionObserver((entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.#observer.disconnect();
            this.load();
          }, { rootMargin: '120px' });
          this.#observer.observe(this.parentElement);
        }
      });
    }

    /** Synchronous so slideshow selection can wait on the mounted image. */
    load() {
      const template = this.querySelector('template[data-product-display-image]');
      if (template) template.replaceWith(template.content.cloneNode(true));
      this.#observeImage();
      if (!this.#image) return;
      this.#image.loading = 'eager';
      this.#slideObserver?.disconnect();
      if (this.#reveal()) return;
      this.#setRetry(false);
      clearTimeout(this.#timeout);
      this.#timeout = setTimeout(() => this.#setRetry(true), 20000);
    }

    #observeImage() {
      const image = this.querySelector('.product-media__image');
      if (!image || !this.#controller || this.#controller.signal.aborted) return;
      if (this.#image !== image) {
        this.#image = image;
        const { signal } = this.#controller;
        image.addEventListener('load', () => this.#reveal(), { signal });
        image.addEventListener('error', () => this.#setRetry(true), { signal });
      }
      this.#reveal();
    }

    #reveal() {
      if (!this.#image?.complete || !this.#image.naturalWidth) return false;
      const image = this.#image;
      const source = image.currentSrc || image.src;
      if (this.#decodedSource !== source) {
        if (this.#decodingSource !== source) {
          this.#decodingSource = source;
          const signal = this.#controller.signal;
          Promise.resolve().then(() => image.decode?.()).catch(() => {
            // A valid, already loaded image can still paint when decode() is
            // unsupported/rejected; a changed or broken source cannot.
          }).then(() => {
            if (signal.aborted || this.#image !== image || (image.currentSrc || image.src) !== source || !image.naturalWidth) return;
            this.#decodedSource = source;
            this.#decodingSource = null;
            this.#reveal();
          });
        }
        return false;
      }
      // Hold the already-decoded card preview for the geometry animation,
      // not for the zoom script. The navigation controller has a bounded exit.
      if (this.hasAttribute('data-navigation-preview') &&
          document.documentElement.hasAttribute('data-product-navigation-active')) return false;
      clearTimeout(this.#timeout);
      this.removeAttribute('data-image-pending');
      this.setAttribute('data-image-ready', '');
      this.removeAttribute('data-navigation-preview');
      this.#setRetry(false);
      return true;
    }

    #setRetry(show) {
      const retry = this.querySelector('.product-media__retry');
      if (retry) retry.hidden = !show;
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#slideObserver?.disconnect();
      clearTimeout(this.#timeout);
      this.#image = null;
      this.#decodingSource = null;
      this.#decodedSource = null;
    }
  });
}
